import React, { useRef, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { observer } from 'mobx-react';
import Icon from '@mdi/react';
import { Accordion } from 'cd-eft/components/common/Accordion';
import { ProjectActions } from '../ProjectActions/ProjectActions';
import { ProjectVM, projectTabs } from './ProjectVM';
import stores from 'cd-eft/models/Stores';
import { ActivityLog } from '../ActivityLog/ActivityLog';
import { AdditionalProjectDetails } from '../AdditionalProjectDetails/AdditionalProjectDetails';
import { ProjectComments } from '../ProjectComments/ProjectComments';
import { ActivitySummary } from '../ActivitySummary/ActivitySummary';
import { DesignComments } from '../DesignComments/DesignComments';
import { FlatButton } from 'nrcs-design-system';
import { mdiMessageReplyText, mdiFolderText, mdiArrowExpandAll, mdiChevronLeft, mdiMapMarkerOutline } from '@mdi/js';
import { TabContent, TabPane, Modal, ModalHeader, ModalBody, Row, Col } from 'reactstrap';
import { ProjectSummary } from '../ProjectSummary/ProjectSummary';
import { Loader } from 'cd-eft/components/common/LoadingSpinner/LoadingSpinner';
import { DesignElementTypeData, imageName } from '../DesignElementTypeData/DesignElementTypeData';
import { formatDateShort } from 'cd-eft/utils/formatDate';
import { Tabs } from 'cd-eft/components/common/Tabs';
import { IWaterwaySummaryStation, ITerraceSummaryStation } from 'cd-eft/components/Projects/Project/ProjectVM';
import { isValidBase64ImageRegex } from 'cd-eft/utils/stringUtilities';
import { UserDefinedImages } from 'cd-eft/_codegen_/openapi-generator/eft-set';
import './project.scss';

const libraryStore = stores.AppStore.projectsLibraryStore;

const ProjectComponent = observer(() => {
  const vm = useRef(new ProjectVM()).current;
  const { routeProjectId }: any = useParams();

  useEffect(() => {
    vm.getProjectData(routeProjectId);
  }, [routeProjectId]);

  useEffect(() => {
    libraryStore.designElementsImageDic = {};
  }, []);

  const [modal, setModal] = useState(false);
  const toggle = () => setModal(!modal);

  const waterwayTypes = {
    7: 'Grassed Waterway',
    19: 'Lined Waterway'
  };

  return (
    <div className="project-container p-3">
      {vm.project.projectUuid && (
        <>
          <h1 className="h2">{vm.project.projectTitle}</h1>

          <Row className="flex-align-stretch align-content-stretch">
            {/* Project Summary */}
            <Col className="mb-3 project-summary-container">
              <ProjectSummary project={vm.project} updateCallback={vm.summaryUpdateCallback} />
            </Col>

            {/* Area of interest */}
            <Col className="mb-3 d-flex align-self-stretch">
              <Col className="container-wrapper area-of-interest-container">
                <div className="area-of-interest__picture" style={{ backgroundImage: `url(${vm.image})` }}></div>
                <div className="text-right">
                  {vm.isImageLoading && <Loader message={'Loading Image ...'} />}

                  {vm.project.projectionText}
                  {vm.image !== '' ? (
                    <button className="expand-image usa-button--unstyled ml-3" onClick={toggle}>
                      <Icon path={mdiArrowExpandAll} title="Expand" size={1} />
                    </button>
                  ) : null}
                </div>
              </Col>
              <Col className="project-action__buttons align-self-center">
                <ProjectActions project={vm.project} fromProjectView={true} />
                <FlatButton title={'Review'} iconLeft={mdiMessageReplyText} onClick={() => { }} disabled={true}>
                  <span>Review</span>
                </FlatButton>
                <FlatButton title={'Approve'} iconLeft={mdiFolderText} onClick={() => { }} disabled={true}>
                  <span>Approve</span>
                </FlatButton>
              </Col>
            </Col>
          </Row>

          {/*For expanding AOI image Modal */}

          {vm.image !== '' ? (
            <Modal isOpen={modal} toggle={toggle} size="lg" centered>
              <ModalHeader toggle={toggle} size="sm"></ModalHeader>
              <ModalBody>
                <div className="area-of-interest__picture">
                  <img src={vm.image} width="100%" height="100%" alt={vm.project.projectionText} />
                </div>
              </ModalBody>
            </Modal>
          ) : null}

          <Tabs tabs={projectTabs} currentTab={vm.activeTab} onClick={vm.changeTab} />

          <TabContent activeTab={vm.activeTab}>
            <TabPane tabId="1">
              {/* Additional Project Details */}
              <AdditionalProjectDetails projectId={routeProjectId} />
            </TabPane>
            <TabPane tabId="2">
              {/* Activity Summary */}
              <ActivitySummary projectId={routeProjectId} />
            </TabPane>
            <TabPane tabId="3">
              {/* Project Level - Project Comments */}
              <ProjectComments projectId={routeProjectId} updateCallback={vm.summaryUpdateCallback} />
              {/* Hydrology/Design project level Comments */}
              <DesignComments
                projectId={routeProjectId}
                projectClassId={vm.projectClassId}
                currentStructureList={vm.project.projectClassId === 1 ? vm.currentStructureList : undefined}
                updateCallback={vm.summaryUpdateCallback}
              />
            </TabPane>
            <TabPane tabId="4">
              {/* Activity log */}
              <ActivityLog projectId={routeProjectId} summaryUpdateVariable={vm.summaryUpdate} />
            </TabPane>
          </TabContent>

          {vm.project.projectClassId === 1 && (
            <>
              {vm.designElementTypes.length > 0 &&
                vm.designElementTypes.map((type) => {
                  const designElementType = type.designElementType!;
                  const mapDesignElementTypeIdToTerraceType = (designElementTypeId: number): string | null => {
                    const mapping: { [key: number]: string } = {
                      20: "GRADIENT",
                      21: "LEVEL",
                      22: "STORAGE",
                      23: "WASCOB",
                    };
                    return mapping[designElementTypeId] || null;
                  };
                  if ([7, 19].includes(type.designElementTypeId!)) {
                    return (
                      <Accordion
                        key={designElementType}
                        className="p-0"
                        expanded={false}
                        headers={[waterwayTypes[type.designElementTypeId!]]}
                        onToggle={vm.getWaterwaySummary}
                      >
                        <WaterwayBox
                          projectId={routeProjectId}
                          WaterwayData={vm.waterwaySummaryList.filter(
                            (el: IWaterwaySummaryStation) => el.waterwayTypeId === type.designElementTypeId
                          )}
                        />
                      </Accordion>
                    );
                  } else if ([20, 21, 22, 23].includes(type.designElementTypeId!)) {
                    const terraceType = mapDesignElementTypeIdToTerraceType(type.designElementTypeId!);
                    if (!terraceType) {
                      return null;
                    }
                    const filteredTerraceData = vm.terraceSummaryList.filter(
                      (el: ITerraceSummaryStation) => el.terraceType === terraceType
                    );
                    return (
                      <Accordion
                        key={designElementType}
                        className="p-0"
                        expanded={false}
                        headers={[`Terrace ${terraceType.charAt(0).toUpperCase() + terraceType.slice(1).toLowerCase()}`]}
                        onToggle={vm.getTerraceSummary}
                      >
                        <TerraceBox
                          projectId={routeProjectId}
                          TerraceData={filteredTerraceData}
                        />
                      </Accordion>
                    );
                  } else {
                    return (
                      <Accordion
                        key={designElementType}
                        className="p-0"
                        expanded={false}
                        headers={[designElementType]}
                        onToggle={async () => vm.onClickAccordion(designElementType)}
                      >
                        <DesignElementTypeData projectId={routeProjectId} type={type} />
                      </Accordion>
                    );
                  }
                })}

              {/* List of Surveys */}
              {vm.surveys.length > 0 && (
                <Accordion className="p-0" expanded={false} headers={['Surveys']}>
                  <SurfaceData projectId={routeProjectId} surveys={vm.surveys} />
                </Accordion>
              )}
            </>
          )}
        </>
      )}

      {!libraryStore.isLoading && !vm.project.projectUuid ? (
        <div className="p-5">
          <Link to="/projects" className="btn-main btn btn-primary">
            <Icon path={mdiChevronLeft} title="SET Icon" size={1} color="white" />
            Projects Library
          </Link>

          <h1 className="py-5 text-center">Project not found</h1>
        </div>
      ) : null}
    </div>
  );
});
export const Project = ProjectComponent;

const SurfaceData = ({ projectId, surveys }) => {
  const surveyTypes = {
    1: 'XYZ',
    2: 'Station Offset Spreadsheet',
    3: 'Station Offset Cross Slope',
  };

  return (
    <>
      {surveys &&
        surveys.map((survey) => (
          <div key={survey.surveyId}>
            <h3>{survey.surveyName} </h3>
            <div>
              <strong>Survey Type:</strong> {surveyTypes[survey.surveyType]}
            </div>
            <div>
              <strong>Date Created:</strong> {survey.surveyDate} (surveyDate from response)
            </div>
            <div>
              <strong>Number of Points:</strong> {survey.pointsCount}
            </div>
            <div>
              <strong>GIS coordinates:</strong> Coordinates here (missing in API response)
            </div>
            <hr />
          </div>
        ))}
      <div className="text-right">
        <Link
          className="quick-link-button bg-white my-2 usa-button usa-button--outline"
          to={`/design/${projectId}/surface`}
        >
          <Icon path={mdiMapMarkerOutline} title="SET Icon" size={1} color="#005ea2" />
          Continue to Survey Workflow
        </Link>
      </div>
    </>
  );
};

interface IwaterwayBox {
  projectId: string;
  WaterwayData: IWaterwaySummaryStation[];
}

export const WaterwayBox = (props: IwaterwayBox) => {
  const { projectId, WaterwayData } = props;

  return (
    <>
      {WaterwayData &&
        WaterwayData.map((waterwayObject) => (
          <div key={waterwayObject.alignedDesignElementId}>
            <h3>{waterwayObject.name}</h3>
            {[
              { label: 'Waterway Type', value: waterwayObject.waterwayType },
              { label: 'Channel Type', value: waterwayObject.channelType },
              { label: 'Length (ft)', value: waterwayObject.length.toFixed(2) },
              { label: 'Max Top Width', value: waterwayObject.maxTopWidth.toFixed(2) },
              { label: 'Number of Reaches', value: waterwayObject.reachCount },
              { label: 'Last Updated Date', value: formatDateShort(Number(waterwayObject.lastUpdatedDate)) },
            ].map((item, idx) => (
              <div className="cross-sections" key={idx}>
                <strong>{item.label}: </strong>
                <span>{item.value}</span>
              </div>
            ))}

            {waterwayObject.imagesDictionary.some((el) => el.imageTypeId == 4) && (
              <div className="images-section">
                <ImagesComponent
                  section="Three cross sections"
                  imagesDictionary={waterwayObject.imagesDictionary
                    .filter((el: UserDefinedImages) => el.imageTypeId == 4)
                    .map((el: UserDefinedImages) => {
                      return { ...el, imageName: 'Station ' + el.imageName };
                    })}
                />
              </div>
            )}

            {waterwayObject.imagesDictionary.some(
              (el: UserDefinedImages) =>
                el.imageTypeId == 0 || el.imageTypeId == 1 || el.imageTypeId == 2 || el.imageTypeId === 3
            ) && (
                <div className="images-section">
                  <ImagesComponent
                    section="Images"
                    imagesDictionary={waterwayObject.imagesDictionary
                      .filter(
                        (el: UserDefinedImages) =>
                          el.imageTypeId == 0 || el.imageTypeId == 1 || el.imageTypeId == 2 || el.imageTypeId === 3
                      )
                      .map((el: UserDefinedImages) => {
                        return { ...el, imageName: imageName[el.imageTypeId!] };
                      })}
                  />
                </div>
              )}

            {waterwayObject.imagesDictionary.some((el: UserDefinedImages) => el.imageTypeId == 5) && (
              <div className="images-section">
                <ImagesComponent
                  section="Single cross section"
                  imagesDictionary={waterwayObject.imagesDictionary.filter(
                    (el: UserDefinedImages) => el.imageTypeId == 5
                  )}
                />
              </div>
            )}
            <hr />
          </div>
        ))}
      <div className="text-right">
        <Link
          className="quick-link-button bg-white my-2 usa-button usa-button--outline"
          to={`/design/${projectId}/waterway/`}
        >
          <Icon path={mdiMapMarkerOutline} title="SET Icon" size={1} color="#005ea2" />
          Continue to Waterway Workflow
        </Link>
      </div>
    </>
  );
};

interface ITerraceBox {
  projectId: string;
  TerraceData: ITerraceSummaryStation[];
}

export const TerraceBox = (props: ITerraceBox) => {
  const { projectId, TerraceData } = props;

  return (
    <>
      {TerraceData &&
        TerraceData.map((terraceObject) => (
          <div key={terraceObject.alignedElementDesignParamsId}>
            <h3>{terraceObject.name}</h3>
            {[
              { label: 'Terrace Type', value: terraceObject.terraceType },
              { label: 'Default Template', value: terraceObject.defaultTemplate || 'N/A' },
              { label: 'Length (ft)', value: terraceObject.length.toFixed(2) },
              { label: 'Max Height (ft)', value: terraceObject.maxHeight.toFixed(2) },
              { label: 'Created Date', value: formatDateShort(terraceObject.createdDate) },
              { label: 'Last Change Date', value: formatDateShort(terraceObject.lastChangeDate) },
            ].map((item, idx) => (
              <div className="cross-sections" key={idx}>
                <strong>{item.label}: </strong>
                <span>{item.value}</span>
              </div>
            ))}

            {/* Display images based on imageTypeId */}
            {terraceObject.imagesDictionary.some((el) => el.imageTypeId === 4) && (
              <div className="images-section">
                <ImagesComponent
                  section="Three cross sections"
                  imagesDictionary={terraceObject.imagesDictionary
                    .filter((el: UserDefinedImages) => el.imageTypeId === 4)
                    .map((el: UserDefinedImages) => {
                      return { ...el, imageName: 'Station ' + el.imageName };
                    })}
                />
              </div>
            )}

            {terraceObject.imagesDictionary.some(
              (el: UserDefinedImages) =>
                el.imageTypeId === 0 || el.imageTypeId === 1 || el.imageTypeId === 2 || el.imageTypeId === 3
            ) && (
                <div className="images-section">
                  <ImagesComponent
                    section="Images"
                    imagesDictionary={terraceObject.imagesDictionary
                      .filter(
                        (el: UserDefinedImages) =>
                          el.imageTypeId === 0 || el.imageTypeId === 1 || el.imageTypeId === 2 || el.imageTypeId === 3
                      )
                      .map((el: UserDefinedImages) => {
                        return { ...el, imageName: imageName[el.imageTypeId!] };
                      })}
                  />
                </div>
              )}

            {terraceObject.imagesDictionary.some((el: UserDefinedImages) => el.imageTypeId === 5) && (
              <div className="images-section">
                <ImagesComponent
                  section="Single cross section"
                  imagesDictionary={terraceObject.imagesDictionary.filter(
                    (el: UserDefinedImages) => el.imageTypeId === 5
                  )}
                />
              </div>
            )}
            <hr />
          </div>
        ))}
      <div className="text-right">
        <Link
          className="quick-link-button bg-white my-2 usa-button usa-button--outline"
          to={`/design/${projectId}/terrace/`}
        >
          <Icon path={mdiMapMarkerOutline} title="SET Icon" size={1} color="#005ea2" />
          Continue to Terrace Workflow
        </Link>
      </div>
    </>
  );
};
export interface ImageLinkProps {
  index: string;
  selected: boolean;
  toggleSelection: (newSelected: boolean, key: string) => void;
  name: string;
}

export const ImageLink: React.FC<ImageLinkProps> = observer(({ index, selected, toggleSelection, name }) => {
  const handleOnClickImageLink = () => {
    toggleSelection(!selected, index!);
  };
  return (
    <button className={`link-button pr-3 ${selected ? 'text-bold' : ''}`} onClick={handleOnClickImageLink}>
      {name}
    </button>
  );
});

export interface ImageDisplayProps {
  index: string;
  base64ImageString: string;
  imageTitle: string;
  isSelected: boolean;
  onExpand: (image: string | null) => void;
  onClose: (newSelected: boolean, index: string) => void;
}

export const ImageDisplay: React.FC<ImageDisplayProps> = observer(
  ({ index, base64ImageString, imageTitle, isSelected, onExpand, onClose }) => {
    if (!isSelected) return null;
    return (
      <div className="image-display pr-3">
        <div>
          <img
            src={base64ImageString}
            alt={imageTitle}
            style={{
              height: '200px',
              width: '300px',
              objectFit: 'cover',
            }}
          />
        </div>
        {imageTitle}
        {/* Buttons to expand and deselect */}
        <div className="button-group pb-3">
          <button className="usa-button usa-button--unstyled" onClick={() => onExpand(base64ImageString)}>
            <Icon path={mdiArrowExpandAll} title="Expand" size={1.3} />
          </button>
          <button className="usa-button usa-button--outline btn-sm" onClick={() => onClose(false, index)}>
            Close
          </button>
        </div>
      </div>
    );
  }
);

export interface ImagesComponentProps {
  imagesDictionary: UserDefinedImages[];
  section?: string;
}

export const ImagesComponent: React.FC<ImagesComponentProps> = observer(({ imagesDictionary, section }) => {
  const initialSelectedLinks = {};
  imagesDictionary.forEach((el: UserDefinedImages) => (initialSelectedLinks[el.imageName!] = false));
  const [selectedLinks, setSelectedLinks] = useState<{ [key: string]: boolean }>(initialSelectedLinks);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const toggleExpand = (image: string | null) => {
    setSelectedImage(image);
    setIsOpen((prev) => !prev);
  };

  const toggleSelection = (newSelected: boolean, key: string) => {
    setSelectedLinks((prevSelectedLinks) => {
      const updatedSelectedLinks = { ...prevSelectedLinks };
      updatedSelectedLinks[key] = newSelected;
      return updatedSelectedLinks;
    });
  };

  return (
    <div className="image-section">
      {/* Render the image links */}
      <div className="imageLinks">
        <strong className="images-section-title">{section}: </strong>
        {imagesDictionary.map((el, index) => {
          const i = `${el.imageName}-${index}`;
          return (
            <ImageLink
              key={i}
              name={el.imageName!}
              index={i}
              selected={selectedLinks[i]}
              toggleSelection={toggleSelection}
            />
          );
        })}
      </div>

      {/* Render the selected images */}
      <div className="imageDisplays">
        {imagesDictionary.map((el, index) => {
          const i = `${el.imageName}-${index}`;
          let base64ImageString: string = '';
          if (isValidBase64ImageRegex(el.images)) {
            base64ImageString = el.images!;
          } else {
            base64ImageString = 'data:image/png;base64,' + el.images;
          }
          return (
            <ImageDisplay
              key={i}
              index={i!}
              base64ImageString={base64ImageString}
              imageTitle={el.imageName!}
              isSelected={selectedLinks[i]}
              onExpand={toggleExpand}
              onClose={toggleSelection}
            />
          );
        })}
      </div>

      {/* Modal to display the expanded image */}
      <Modal isOpen={isOpen} toggle={() => toggleExpand(null)} size="lg" centered>
        <ModalHeader toggle={() => toggleExpand(null)}></ModalHeader>
        <ModalBody>
          {selectedImage && (
            <img src={selectedImage} alt="Expanded view" style={{ maxWidth: 'none', width: '800px' }} />
          )}
        </ModalBody>
      </Modal>
    </div>
  );
});
